import { useState } from "react";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { useAppSelector, useAppDispatch } from "hooks/store";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { setdeliveryType } from "store/slices/main.slice";

interface OrderTypePopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (orderType: "pickup" | "delivery") => void;
}

const OrderTypePopup: React.FC<OrderTypePopupProps> = ({ open, onClose }) => {
  const [orderType, setOrderType] = useState<"pickup" | "delivery">("pickup");
  const dispatch = useAppDispatch();
  const { settings, companyData } = useAppSelector(({ main }) => main);

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // Prevent closing the modal when clicking the backdrop (background area)
  const handleModalClose = (_event: any, reason?: string) => {
    if (reason === "backdropClick") return; // disable background click close
    onClose(); // keep other close behaviors (e.g., ESC) working
  };
  // === END SAMPATH ===

  const handleSubmit = () => {
    dispatch(setdeliveryType(orderType));
    onClose();
  };

  return (
    <Modal
      open={open}
      // === ADDED BY SAMPATH – 2026-01-05 ===
      onClose={handleModalClose}
      // === END SAMPATH ===
      className="flex items-center justify-center"
      /* ============================================================
         Glass Backdrop added by Sampath – 2026-01-05
         ============================================================ */
      slotProps={{
        backdrop: {
          onClick: (e) => e.stopPropagation(),
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.69)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
        },
      }}
      /* ============================================================ */
    >
      {/* ============================================================
          Glass Container enhancement by Sampath – 2026-01-05
          (Original bg-white replaced with glass UI)
         ============================================================ */}
      <div className="bg-white/90 backdrop-blur-xl border border-white/40 p-6 rounded-2xl shadow-2xl max-w-sm w-full">
        {/* ============================================================ */}

        <div className="flex flex-col items-center">
          {settings?.showLogo !== 0 ? (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="text-white !px-1.5 relative"
            >
              {/* === ADDED BY SAMPATH – 2026-01-05 === */}
              {/* Clean logo — no box, no background, no distortion */}
              <img
                src={companyData?.logoUrl}
                alt=""
                className="mb-4 max-h-20 max-w-[200px] object-contain"
                loading="eager"
                draggable={false}
              />
              {/* === END SAMPATH === */}
            </div>
          ) : (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="text-white !px-1.5 relative bg-white/40 p-2 rounded-xl shadow"
            >
              <AdminPanelSettingsIcon className="text-primary" />
            </div>
          )}

          {!!settings.ShowCompanyName && !!companyData?.companyName && (
            <h1 className="text-xl font-bold text-primary">
              {companyData?.companyName}
            </h1>
          )}
        </div>

        {/* ============================================================
            Glass pill toggle background – Sampath 2026-01-05
           ============================================================ */}
        <div className="flex gap-2 bg-white/95 border border-white/40 rounded-full p-1 mt-4 shadow-inner">
          <button
            className={`${
              orderType === "pickup"
                ? "bg-primary text-themeSelColor shadow"
                : "bg-transparent text-gray-800"
            } py-2 px-4 transition-colors duration-300 rounded-full flex-1`}
            onClick={() => setOrderType("pickup")}
          >
            Pickup
          </button>

          <button
            className={`${
              orderType === "delivery"
                ? "bg-primary text-themeSelColor shadow"
                : "bg-transparent text-gray-800"
            } py-2 px-4 transition-colors duration-300 rounded-full flex-1`}
            onClick={() => setOrderType("delivery")}
          >
            Delivery
          </button>
        </div>
        {/* ============================================================ */}

        {/* Continue button – Original theme preserved */}
        <Button
          onClick={handleSubmit}
          disabled={!orderType}
          variant="contained"
          className="!mt-6 w-full !bg-primary !text-themeSelColor !rounded-xl !py-3 !shadow-lg"
        >
          Continue
        </Button>
      </div>
    </Modal>
  );
};

export default OrderTypePopup;
